// Settings service for admin panel
import { supabase } from '@/integrations/supabase/client';

// Firm settings interface
export interface FirmSettings {
  businessName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  email: string;
  website: string;
}

// Business hours interface
export interface BusinessHours {
  monday: { open: string; close: string; closed: boolean };
  tuesday: { open: string; close: string; closed: boolean };
  wednesday: { open: string; close: string; closed: boolean };
  thursday: { open: string; close: string; closed: boolean };
  friday: { open: string; close: string; closed: boolean };
  saturday: { open: string; close: string; closed: boolean };
  sunday: { open: string; close: string; closed: boolean };
  timezone: string;
}

// Notification settings interface
export interface NotificationSettings {
  emailNewBookings: boolean;
  emailPaymentReceived: boolean;
  emailAppointmentReminders: boolean;
  emailDailyDigest: boolean;
}

// Default firm settings
export const DEFAULT_FIRM_SETTINGS: FirmSettings = {
  businessName: 'Daniel Albert Law Firm',
  address: '1800 Bering Dr., Suite 1050',
  city: 'Houston',
  state: 'TX',
  zipCode: '77057',
  phone: '(713) 999-1818',
  email: 'info@danielalbertlaw.com',
  website: 'https://danielalbertlaw.com',
};

// Default business hours
export const DEFAULT_BUSINESS_HOURS: BusinessHours = {
  monday: { open: '09:00', close: '17:00', closed: false },
  tuesday: { open: '09:00', close: '17:00', closed: false },
  wednesday: { open: '09:00', close: '17:00', closed: false },
  thursday: { open: '09:00', close: '17:00', closed: false },
  friday: { open: '09:00', close: '17:00', closed: false },
  saturday: { open: '10:00', close: '14:00', closed: false },
  sunday: { open: '00:00', close: '00:00', closed: true },
  timezone: 'America/Chicago',
};

// Default notification settings
export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  emailNewBookings: true,
  emailPaymentReceived: true,
  emailAppointmentReminders: true,
  emailDailyDigest: false,
};

// Admin user interface
export interface AdminUser {
  id: string;
  user_id: string;
  email: string;
  role: 'superadmin' | 'admin' | 'editor' | 'user';
  created_at: string;
  last_sign_in?: string;
}

// Get all admin users with their roles
export async function getAdminUsers(): Promise<AdminUser[]> {
  const { data, error } = await supabase.functions.invoke('manage-admin-users', {
    body: { action: 'list' }
  });

  if (error) {
    console.error('Error fetching admin users:', error);
    throw error;
  }

  return data?.users || [];
}

// Create new admin user
export async function createAdminUser(email: string, password: string, role: string): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke('manage-admin-users', {
    body: { action: 'create', email, password, role }
  });

  if (error) {
    console.error('Error creating admin user:', error);
    return { success: false, error: error.message };
  }

  return data;
}

// Update user role
export async function updateUserRole(userId: string, role: string): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke('manage-admin-users', {
    body: { action: 'updateRole', userId, role }
  });

  if (error) {
    console.error('Error updating user role:', error);
    return { success: false, error: error.message };
  }

  return data;
}

// Delete admin user
export async function deleteAdminUser(userId: string): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke('manage-admin-users', {
    body: { action: 'delete', userId }
  });

  if (error) {
    console.error('Error deleting admin user:', error);
    return { success: false, error: error.message };
  }

  return data;
}

// Change password for current user
export async function changePassword(newPassword: string): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase.auth.updateUser({
    password: newPassword
  });

  if (error) {
    console.error('Error changing password:', error);
    return { success: false, error: error.message };
  }

  return { success: true };
}

// Role options
export const ROLE_OPTIONS = [
  { value: 'superadmin', label: 'Super Admin', description: 'Full access to all features' },
  { value: 'admin', label: 'Administrator', description: 'Access to most features except user management' },
  { value: 'editor', label: 'Editor', description: 'Can edit blog posts and pages' },
  { value: 'user', label: 'User', description: 'Basic access, no admin panel' },
];

// Get role badge color
export function getRoleBadgeVariant(role: string): 'default' | 'secondary' | 'outline' | 'destructive' {
  switch (role) {
    case 'superadmin':
      return 'default';
    case 'admin':
      return 'secondary';
    case 'editor':
      return 'outline';
    default:
      return 'outline';
  }
}
